import { useTranslations } from '@vocab/react';
import {
  Box,
  Drawer,
  Stack,
  Button,
  IconSettings,
  useResponsiveValue,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, useState, useEffect } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

import type { IJob } from '../../../../stores/JobStore/Job';
import type { JobAction } from '../actions';
import { useJobActionOptions } from '../hooks/useJobActionOptions';

import translations from './.vocab';
import IconRow from './IconRow';

interface Props {
  job: IJob;
  isRecommendedCandidatesTabShown: boolean;
  onAction: (jobAction: JobAction) => void;
}

const JobActionMobile: FC<Props> = ({
  job,
  isRecommendedCandidatesTabShown,
  onAction,
}) => {
  const { t } = useTranslations(translations);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { site } = useAppConfig();
  const jobActionOptions = useJobActionOptions({
    job,
    site,
    showTalentSearchLink: !isRecommendedCandidatesTabShown && job.isActive,
    showExportToCsv: false,
  });
  const isMobile =
    useResponsiveValue()({ mobile: true, tablet: false }) ?? false;

  useEffect(() => {
    if (!isMobile && drawerOpen) {
      setDrawerOpen(false);
    }
  }, [isMobile, drawerOpen, setDrawerOpen]);
  return (
    <Box>
      <Button
        onClick={() => setDrawerOpen(true)}
        size="small"
        tone="neutral"
        variant="soft"
        aria-label={t('Manage job')}
      >
        <IconSettings tone="secondary" />
      </Button>

      <Drawer
        id="job-actions"
        title={t('Manage job')}
        open={drawerOpen}
        width="small"
        onClose={() => setDrawerOpen(false)}
      >
        <Stack space="medium">
          {jobActionOptions.map((option) => (
            <IconRow
              key={option.value}
              icon={option.icon}
              label={option.label}
              onClick={() => {
                setDrawerOpen(false);
                onAction(option.value);
              }}
              disabled={option.disabled}
              disableDescription={option.disableDescription}
              content={option.content}
            />
          ))}
        </Stack>
      </Drawer>
    </Box>
  );
};

export default observer(JobActionMobile);
